import { useState, useEffect } from 'react';
import { useI18n } from '../i18n.jsx';
import { authHeaders } from '../lib/api.js';
import { Shield, Plus, X } from 'lucide-react';

// --- Global Calculator Widget ---
const ManageUsersTab = ({ orgId, backendUrl, currentMember, onMembersChange }) => {
  const { t } = useI18n();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null); // null for new
  const [form, setForm] = useState({ name: '', role: 'VIEWER', pin_code: '', is_active: true, permissions: {} });
  const [error, setError] = useState('');

  const fetchMembers = async () => {
    try {
      const res = await fetch(`${backendUrl}/members/`, { headers: authHeaders({ 'X-Org-Id': orgId }) });
      if (res.ok) {
        const data = await res.json();
        setMembers(data);
        if (onMembersChange) onMembersChange(data.filter(m => m.is_active));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [orgId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const url = editingMember ? `${backendUrl}/members/${editingMember.id}/` : `${backendUrl}/members/`;
      const method = editingMember ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: authHeaders({ 'Content-Type': 'application/json', 'X-Org-Id': orgId }),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok) {
        setShowModal(false);
        fetchMembers();
      } else {
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleDeactivate = async (id, isActive) => {
    if (!confirm(isActive ? (t('deactivateConfirm') || 'Deactivate this member?') : (t('activateConfirm') || 'Activate this member?'))) return;
    try {
      const res = await fetch(`${backendUrl}/members/${id}/`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json', 'X-Org-Id': orgId }),
        body: JSON.stringify({ is_active: !isActive })
      });
      if (res.ok) fetchMembers();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div>{t('loadingUsers') || 'Loading users...'}</div>;

  return (
    <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '800px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Shield size={24} color="var(--brand-info)" />
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('manageUsers') || 'Manage Users'}</h3>
        </div>
        <button onClick={() => { setEditingMember(null); setForm({ name: '', role: 'VIEWER', pin_code: '', is_active: true, permissions: {} }); setShowModal(true); }} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
          <Plus size={16} /> {t('addUser') || 'Add User'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {members.map(m => (
          <div key={m.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '16px', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: m.role === 'ADMIN' ? 'linear-gradient(135deg, #10b981, #38bdf8)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 700, color: 'white' }}>{m.name.charAt(0).toUpperCase()}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '1rem', color: m.is_active ? 'var(--text-primary)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {m.name}
                  {!m.is_active && <span style={{ fontSize: '0.7rem', padding: '2px 6px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: '4px' }}>{t('inactive') || 'Inactive'}</span>}
                  {currentMember && currentMember.member_id === m.id && <span style={{ fontSize: '0.7rem', padding: '2px 6px', background: 'rgba(16,185,129,0.1)', color: '#10b981', borderRadius: '4px' }}>{t('you') || 'You'}</span>}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }}>{t('roleLabel') || 'Role:'} {m.role} &nbsp;•&nbsp; {t('pinLabel') || 'PIN:'} {m.pin_set ? (t('set') || 'Set') : (t('notSet') || 'Not set')}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { setEditingMember(m); setForm({ name: m.name, role: m.role, pin_code: '', is_active: m.is_active, permissions: m.permissions }); setShowModal(true); }} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>{t('edit') || 'Edit'}</button>
              {!(currentMember && currentMember.member_id === m.id) && m.role !== 'ADMIN' && (
                <button onClick={() => handleDeactivate(m.id, m.is_active)} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.8rem', color: m.is_active ? '#ef4444' : '#10b981' }}>{m.is_active ? (t('deactivate') || 'Deactivate') : (t('activate') || 'Activate')}</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1101, background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-content" style={{ maxWidth: '400px', zIndex: 1102 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>{editingMember ? (t('editUser') || 'Edit User') : (t('addUser') || 'Add User')}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            {error && <div style={{ padding: '10px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: '6px', marginBottom: '16px', fontSize: '0.85rem' }}>{error}</div>}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '6px' }}>{t('nameLabel') || 'Name'}</label>
                <input required type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input-field" style={{ width: '100%' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '6px' }}>{t('roleLabel') || 'Role'}</label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="input-field" style={{ width: '100%' }} disabled={editingMember && editingMember.role === 'ADMIN'}>
                  <option value="ADMIN">{t('adminRole') || 'Admin (Full Access)'}</option>
                  <option value="MANAGER">{t('managerRole') || 'Manager'}</option>
                  <option value="CASHIER">{t('cashierRole') || 'Cashier (Cash + Ledger)'}</option>
                  <option value="VIEWER">{t('viewerRole') || 'Viewer (Read Only)'}</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '6px' }}>{editingMember ? (t('newPinHint') || 'New PIN (leave blank to keep current)') : (t('pinHint') || 'PIN (4 digits)')}</label>
                <input type="password" maxLength={4} required={!editingMember} value={form.pin_code} onChange={e => setForm({...form, pin_code: e.target.value.replace(/\D/g, '')})} className="input-field" style={{ width: '100%', letterSpacing: '0.2em' }} placeholder="••••" />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{editingMember ? (t('saveChanges') || 'Save Changes') : (t('addUser') || 'Add User')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsersTab;
